import type { IBoostTAE } from '@boost/widget-integration-tae/lib';
import { Application, Module, ModuleRefImpl, type OnApplicationBootstrap } from 'framework/core';
import { Constructor } from 'framework/di';
import { PerformanceService } from 'framework/services';

import { initAnalytic } from '../../analytic';
import { initApp } from '../../app';
import { SESSION_KEY } from '../../constants/app';
import { templateManagement } from '../../template/template-management';
import { requestIdleCallback } from '../../utils';
import { B2BModule } from '../b2b/b2b.module';
import { CartModule } from '../cart/cart.module';
import { FilterModule } from '../filter/filter.module';
import { PlatformModule } from '../platform/platform.module';
import { RecentlyViewedProductModule } from '../recently-viewed-product/recently-viewed-product.module';
import { RecommendationModule } from '../recommendation/recommendation.module';
import { AppService } from './app.service';

@Module({
  imports: [
    PlatformModule,
    CartModule,
    RecommendationModule,
    FilterModule,
    RecentlyViewedProductModule,
    B2BModule,
  ],
  providers: [AppService, PerformanceService],
})
export class AppModule implements OnApplicationBootstrap {
  TAEApp: IBoostTAE;
  performanceService: PerformanceService;

  constructor(public moduleRef: ModuleRefImpl) {
    this.performanceService = this.moduleRef.get<PerformanceService>(PerformanceService);
    this.TAEApp = this.moduleRef.get<AppService>(AppService).TAEApp;
  }

  async onApplicationBootstrap() {
    const { performanceService, TAEApp } = this;
    performanceService.start();

    window.boostWidgetIntegration.generateUuid = function () {
      return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        return r.toString(16);
      });
    };

    window.boostWidgetIntegration.getSessionId = function () {
      try {
        let sessionId = localStorage.getItem(SESSION_KEY);

        if (!sessionId) {
          sessionId = window.boostWidgetIntegration.generateUuid();
          localStorage.setItem(SESSION_KEY, sessionId);
        }

        return sessionId;
      } catch {
        return window.boostWidgetIntegration.generateUuid();
      }
    };

    TAEApp.addRegistryBlockListener((block) => {
      const { blocks } = TAEApp;

      if (blocks[block.id]) {
        performanceService.measureSync(`Initialize ${block.id} block`, () => {
          initApp(blocks[block.id]);
        });
      }
    });

    requestIdleCallback(() => {
      initAnalytic();
    });

    if (Object.keys(TAEApp.blocks).length > 0) {
      setTimeout(() => {
        templateManagement(Object.values(TAEApp.blocks)[0]);
      }, 30000);
    }
  }
}

export function getInstance<Module>(module: Constructor<Module>): Module | null {
  const app = window.boostWidgetIntegration.App as Application;
  return app.get(module);
}
